import API from 'services/api';
import SourcesService from 'services/sources';
import { DEVELOPMENT_STATUS_VALUES } from './constants';

const api = new API();
const sourcesService = new SourcesService();

const unwrapLegacyResponse = response => {
    if (response.status !== 0) {
        return Promise.reject(response.value);
    }
    return response.value;
};

export const buildAppsUrl = ({
    syncApsalarApps = false,
    developmentStatusesWhitelist = DEVELOPMENT_STATUS_VALUES,
} = {}) => {
    const params = new URLSearchParams();
    params.append('sync_apsalar_apps', syncApsalarApps ? 'true' : 'false');
    developmentStatusesWhitelist.forEach(status => {
        params.append('development_statuses_whitelist', status);
    });
    return `/api/apps/apps?${params.toString()}`;
};

export const getApps = options =>
    api
        .getJson(buildAppsUrl(options), {
            method: 'GET',
            credentials: 'include',
            cache: false,
            unique: true,
            channel: 'apps-list',
        })
        .then(unwrapLegacyResponse);

export const getPlatforms = () =>
    api
        .getJson('/api/apps/platforms', {
            method: 'GET',
            credentials: 'include',
            cache: false,
        })
        .then(unwrapLegacyResponse);

export const getStoreDetails = storeUrl =>
    api
        .getJson('/api/apps/store_details', {
            method: 'POST',
            credentials: 'include',
            body: { store_url: storeUrl },
            cache: false,
        })
        .then(unwrapLegacyResponse);

export const getConfiguration = () => sourcesService.getAttributionConfiguration();

export const saveNewApp = name =>
    api
        .getJson('/api/apps/app', {
            method: 'POST',
            credentials: 'include',
            body: { name },
            cache: false,
            fixErr: true,
            legacyStatus: true,
            unique: true,
            channel: 'apps-save-app',
        })
        .then(unwrapLegacyResponse);

export const saveApp = (appId, name) =>
    api
        .getJson(`/api/apps/app/${appId}`, {
            method: 'POST',
            credentials: 'include',
            body: { name },
            cache: false,
            fixErr: true,
            legacyStatus: true,
            unique: true,
            channel: 'apps-save-app',
        })
        .then(unwrapLegacyResponse);

export const deleteApp = appId =>
    api
        .getJson(`/api/apps/app/${appId}`, {
            method: 'DELETE',
            credentials: 'include',
            cache: false,
            unique: true,
            channel: 'apps-delete-app',
        })
        .then(unwrapLegacyResponse);

const getSaveSiteRequestKey = (appId, site) =>
    [appId, site.id || site._clientKey || site.longname || site.url || site.platform || 'new'].join(':');

export const saveSite = (appId, site, requestKey = null) => {
    const url = site.id ? `/api/apps/site/${site.id}` : '/api/apps/site';
    return api
        .getJson(url, {
            method: 'POST',
            credentials: 'include',
            body: { app_id: appId, site },
            cache: false,
            fixErr: true,
            legacyStatus: true,
            unique: true,
            channel: 'apps-save-site',
            suffix: requestKey || getSaveSiteRequestKey(appId, site),
        })
        .then(unwrapLegacyResponse);
};

export const deleteSite = siteId =>
    api
        .getJson(`/api/apps/site/${siteId}`, {
            method: 'DELETE',
            credentials: 'include',
            cache: false,
            unique: true,
            channel: 'apps-delete-site',
        })
        .then(unwrapLegacyResponse);

export const moveSite = (siteId, destinationAppId) =>
    api
        .getJson(`/api/apps/sites/move/${siteId}`, {
            method: 'POST',
            credentials: 'include',
            body: { destination_app_id: destinationAppId },
            cache: false,
            unique: true,
            channel: 'apps-move-site',
        })
        .then(unwrapLegacyResponse);

export const setSiteDevelopmentStatus = (siteId, developmentStatus) =>
    api
        .getJson(`/api/apps/sites/set_development_status/${siteId}`, {
            method: 'POST',
            credentials: 'include',
            body: { development_status: developmentStatus },
            cache: false,
            unique: true,
            channel: 'apps-site-status',
        })
        .then(unwrapLegacyResponse);

export const restoreSite = siteId =>
    api
        .getJson(`/api/apps/sites/restore/${siteId}`, {
            method: 'POST',
            credentials: 'include',
            body: {},
            cache: false,
            unique: true,
            channel: 'apps-restore-site',
        })
        .then(unwrapLegacyResponse);

export const testFcmIntegration = fcmProjectId =>
    api
        .getJson('/api/apps/fcm_integration_test', {
            method: 'POST',
            credentials: 'include',
            body: { fcm_project_id: fcmProjectId },
            cache: false,
            unique: true,
            channel: 'apps-fcm-test',
        })
        .then(unwrapLegacyResponse);
